import {
  COOKIE_BANNER_TOKENS,
  type CookieBannerTheme,
  type CookieBannerTokenKey,
  resolveStorefrontTokens,
  type StorefrontLegal,
} from "@loot-os/api-contract";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { CookieBannerPreview, CookieBannerThemeForm } from "../components/CookieBannerThemeForm";
import { InheritOrCustomize } from "../components/InheritOrCustomize";
import { LegalIdentityForm } from "../components/LegalIdentityForm";
import { StorefrontSection } from "../components/StorefrontSection";
import { TermlyWebsiteForm } from "../components/TermlyWebsiteForm";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * What "Customize" starts from: nothing. A legal identity is a different company, not a tweak of
 * the platform's, so the form never offers the platform's DMCA agent, address or forum as a
 * value a tenant could save by filling in only the obvious fields.
 */
const EMPTY_LEGAL: StorefrontLegal = {
  legalName: "",
  supportEmail: "",
  legalEmail: "",
  postalAddress: "",
  governingLaw: "",
  arbitrationVenue: "",
};

/** The identity as this page summarizes it: the entity and where shoppers write. */
function LegalSummary({ legal }: { legal: StorefrontLegal }) {
  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      <div>
        <dt className="text-xs text-muted-foreground">Legal name</dt>
        <dd>{legal.legalName}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Support email</dt>
        <dd>{legal.supportEmail}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Legal email</dt>
        <dd>{legal.legalEmail || legal.supportEmail}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Postal address</dt>
        <dd>{legal.postalAddress}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Governing law</dt>
        <dd>{legal.governingLaw}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Arbitration venue</dt>
        <dd>{legal.arbitrationVenue}</dd>
      </div>
    </dl>
  );
}

/**
 * Legal / Consent: who this storefront's Terms of Service and Privacy Policy name, whose Termly
 * website its cookie banner runs on, and how that banner looks. Three inherit-or-customize
 * pairs, each inheriting the platform default until an editor customizes it, with "Use platform
 * default" going back. The policy pages read the resolved config, so a legal save reaches
 * shoppers within the config cache's minute. Whose banner, never whether: every storefront shows
 * one, so there is no off switch here.
 */
export function StorefrontLegalPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const { queryOptions, query } = useStorefrontConfigQuery();
  const [customizingLegal, setCustomizingLegal] = useState(false);
  const [customizingTermly, setCustomizingTermly] = useState(false);
  const [customizingTheme, setCustomizingTheme] = useState(false);

  const saveLegal = useMutation(
    trpc.storefront.setLegal.mutationOptions({
      onSuccess: (saved) => {
        setCustomizingLegal(false);
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success(
          saved.legal === null ? "Using the platform default." : "Legal identity saved.",
        );
      },
    }),
  );

  const saveTermly = useMutation(
    trpc.storefront.setTermly.mutationOptions({
      onSuccess: (saved) => {
        setCustomizingTermly(false);
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success(
          saved.websiteUuid === null ? "Using the platform default." : "Termly website saved.",
        );
      },
    }),
  );

  const saveTheme = useMutation(
    trpc.storefront.setTermlyTheme.mutationOptions({
      onSuccess: (saved) => {
        setCustomizingTheme(false);
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success(saved.theme === null ? "Using the platform default." : "Banner style saved.");
      },
    }),
  );

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config, legal, termly, environment } = query.data;
  const ownLegal = config.legal;
  const ownTermly = config.termlyWebsiteUuid;
  const ownTheme = config.cookieBannerTheme;
  const canEdit = can("editor");
  const submitLegal = (next: StorefrontLegal | null) => saveLegal.mutate({ legal: next });
  const submitTermly = (websiteUuid: string | null) => saveTermly.mutate({ websiteUuid });
  const submitTheme = (theme: CookieBannerTheme | null) => saveTheme.mutate({ theme });
  // What an unset banner token shows on this page: this store's own palette, which is what the
  // banner inherits for it.
  const storeTokens = resolveStorefrontTokens(config.theme.tokens);
  const fallbacks = Object.fromEntries(
    COOKIE_BANNER_TOKENS.map((token) => [token.key, storeTokens[token.fallback]]),
  ) as Record<CookieBannerTokenKey, string>;

  return (
    <StorefrontSection
      title="Legal / Consent"
      description="The company this storefront's Terms of Service and Privacy Policy name, and the cookie banner shoppers see: the Termly website whose consent log records their choices, styled like the rest of the store."
      environment={environment}
    >
      <InheritOrCustomize
        title="Legal identity"
        inherited={ownLegal === null}
        customizing={customizingLegal}
        canEdit={canEdit}
        saving={saveLegal.isPending}
        onCustomize={() => setCustomizingLegal(true)}
        onCancel={() => setCustomizingLegal(false)}
        onRevert={() => submitLegal(null)}
        descriptions={{
          inherited:
            "This storefront's terms currently name the platform's company. Customize it so they name yours; the pages pick the change up within a minute.",
          customizing:
            "Every field is your company's own; nothing is saved until all of them are filled and you save.",
          customized:
            "This environment's own identity; the platform default no longer applies here.",
        }}
        inheritedSummary={<LegalSummary legal={legal.platformDefault} />}
      >
        {(revert) => (
          <LegalIdentityForm
            key={ownLegal ? JSON.stringify(ownLegal) : "unsaved"}
            legal={ownLegal ?? EMPTY_LEGAL}
            canEdit={canEdit}
            saving={saveLegal.isPending}
            onSave={submitLegal}
            footer={revert}
          />
        )}
      </InheritOrCustomize>

      <InheritOrCustomize
        title="Cookie consent"
        inherited={ownTermly === null}
        customizing={customizingTermly}
        canEdit={canEdit}
        saving={saveTermly.isPending}
        onCustomize={() => setCustomizingTermly(true)}
        onCancel={() => setCustomizingTermly(false)}
        onRevert={() => submitTermly(null)}
        descriptions={{
          inherited:
            "This storefront runs the platform's Termly website. Customize it to record consents on a Termly website of your own; shoppers pick the change up on their next visit.",
          customizing: "Your own Termly website's id; nothing is saved until you save.",
          customized:
            "This environment's own Termly website; the platform default no longer applies here.",
        }}
        inheritedSummary={
          termly.platformDefaultWebsiteUuid ? (
            <p className="text-sm">
              Termly website{" "}
              <code className="font-mono text-xs">{termly.platformDefaultWebsiteUuid}</code>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              The platform has set no Termly website yet, so shoppers see no cookie banner.
            </p>
          )
        }
      >
        {(revert) => (
          // Starts blank, not from the platform's id: the override exists so the tenant's own
          // Termly website records the consents, and a saved copy of the platform's id would
          // silently stop following it.
          <TermlyWebsiteForm
            key={ownTermly ?? "unsaved"}
            websiteUuid={ownTermly ?? ""}
            placeholder={termly.platformDefaultWebsiteUuid || undefined}
            canEdit={canEdit}
            saving={saveTermly.isPending}
            onSave={submitTermly}
            footer={revert}
          />
        )}
      </InheritOrCustomize>

      <InheritOrCustomize
        title="Banner style"
        inherited={ownTheme === null}
        customizing={customizingTheme}
        canEdit={canEdit}
        saving={saveTheme.isPending}
        onCustomize={() => setCustomizingTheme(true)}
        onCancel={() => setCustomizingTheme(false)}
        onRevert={() => submitTheme(null)}
        descriptions={{
          inherited:
            "The banner takes the platform's colors, and where the platform sets none, this store's own palette. Customize it to give this environment its own.",
          customizing:
            "Started from the platform's colors; nothing is saved until you save. A color left blank follows this store's palette.",
          customized:
            "This environment's own banner colors; the platform default no longer applies here.",
        }}
        inheritedSummary={
          <CookieBannerPreview theme={termly.platformDefaultTheme} fallbacks={fallbacks} />
        }
      >
        {(revert) => (
          // Seeded from the platform theme, unlike the website id: a blank form would silently
          // drop every platform color the tenant did not mean to change.
          <CookieBannerThemeForm
            key={ownTheme ? JSON.stringify(ownTheme) : "unsaved"}
            theme={ownTheme ?? termly.platformDefaultTheme}
            fallbacks={fallbacks}
            canEdit={canEdit}
            saving={saveTheme.isPending}
            onSave={submitTheme}
            footer={revert}
          />
        )}
      </InheritOrCustomize>
    </StorefrontSection>
  );
}
